import i18n from 'i18next';
import type { TourStep, TourStepPlacement } from './types';
import { legacyPathFor } from '@/config/movedPages';

export type TourId =
  | 'account_hub'
  | 'home'
  | 'money'
  | 'add_money'
  | 'cash_out'
  | 'help'
  | 'me'
  | 'finance_deposit'
  | 'finance_wallet'
  | 'wallet_topup'
  | 'wallet_payout'
  | 'games'
  | 'settings'
  | 'account_security'
  | 'animations'
  | 'transactions'
  | 'support';

export interface TourDefinition {
  id: TourId;
  version: string;
  welcomeTitle: string;
  welcomeDescription: string;
  steps: TourStep[];
}

/**
 * The page tour for a path. A path may carry its query: Help's "Ask us" view (`/help?tab=new`) has
 * the ticket form's tour, the Help hub its own.
 */
export function getTourForPath(path: string): TourDefinition | null {
  const [rawPath, query = ''] = path.split('?');
  const normalized = rawPath.replace(/\/+$/, '') || '/';

  // The four tabs' hubs and the guided money flows.
  if (normalized === '/money') return moneyTour();
  if (normalized === '/money/add') return addMoneyTour();
  if (normalized === '/money/cash-out') return cashOutTour();
  if (normalized === '/me') return meTour();
  if (normalized === '/help') return new URLSearchParams(query).get('tab') === 'new' ? askUsTour() : helpTour();
  // A ticket conversation has no tour (the ticket form's tour moved with the form to Help → Ask us).
  if (normalized.startsWith('/help/')) return null;

  // Pages that moved under Money and Me keep their tour (matched below by old path).
  const pathname = legacyPathFor(normalized);
  if (pathname === '/' || pathname === '') return homeTour;

  // More specific wallet paths before the wallet hub.
  if (
    pathname === '/account/finance/wallet/deposit' ||
    pathname.startsWith('/account/wallet/deposit-checkout') ||
    pathname === '/account/wallet/add-funds'
  ) {
    return walletTopUpTour;
  }
  if (pathname === '/account/wallet/payout-request') return walletPayoutTour;
  if (pathname === '/account/finance/wallet') return walletTour;

  if (pathname === '/account/finance/deposit') return depositTour;
  if (pathname === '/account/games' || pathname === '/account/finance/withdrawals') return gamesTour();
  if (pathname === '/account/settings') return settingsTour;
  if (pathname === '/account/security' || pathname === '/account/change-password') return accountSecurityTour;
  if (pathname === '/account/animations') return animationsTour;
  if (pathname === '/account/transactions') return transactionsTour;
  return null;
}

/**
 * The app tour (header and tabs), first after sign-in: the four tabs, "+ Add money", the balance
 * and the bell. Help's "Take the tour" starts it too ("show me around").
 */
export function getPostLoginTour(): TourDefinition {
  return chromeTour();
}

const v1 = 'v1';
const v2 = 'v2';

interface TranslatedStep {
  id: string;
  targetTourId: string;
  placement?: TourStepPlacement;
}

/**
 * A tour whose copy lives under `tour.<key>` in the locale files (en + es): `welcomeTitle`,
 * `welcomeDescription` and `steps.<step id>.title|description`. Read each time the tour is asked
 * for, so it follows the language.
 */
function translatedTour(id: TourId, version: string, key: string, steps: TranslatedStep[]): TourDefinition {
  const t = (path: string) => i18n.t(`tour.${key}.${path}`);
  return {
    id,
    version,
    welcomeTitle: t('welcomeTitle'),
    welcomeDescription: t('welcomeDescription'),
    steps: steps.map((step) => ({
      ...step,
      title: t(`steps.${step.id}.title`),
      description: t(`steps.${step.id}.description`),
    })),
  };
}

/**
 * Header and tabs (Play, Money, Help, Me on the top tabs or the bottom bar; "+ Add money", the
 * balance, the bell). v3: the four tabs replaced Home / Games / Wallet / Activity and the account menu.
 */
const chromeTour = () =>
  translatedTour('account_hub', 'v3', 'app', [
    { id: 'play', targetTourId: 'nav.play', placement: 'auto' },
    { id: 'money', targetTourId: 'nav.money', placement: 'auto' },
    { id: 'help', targetTourId: 'nav.help', placement: 'auto' },
    { id: 'me', targetTourId: 'nav.me', placement: 'auto' },
    { id: 'addMoney', targetTourId: 'header.add_money', placement: 'bottom' },
    { id: 'balance', targetTourId: 'header.balance', placement: 'bottom' },
    { id: 'bell', targetTourId: 'header.bell', placement: 'bottom' },
  ]);

/** Money tab (/money). */
const moneyTour = () =>
  translatedTour('money', v1, 'money', [
    { id: 'addMoney', targetTourId: 'money.add_money', placement: 'bottom' },
    { id: 'cashOut', targetTourId: 'money.cash_out', placement: 'bottom' },
    { id: 'where', targetTourId: 'money.where', placement: 'top' },
    { id: 'history', targetTourId: 'money.history', placement: 'top' },
    { id: 'more', targetTourId: 'money.more', placement: 'top' },
  ]);

/** Guided Add money (/money/add): the targets are on every step. */
const addMoneyTour = () =>
  translatedTour('add_money', v1, 'addMoney', [
    { id: 'steps', targetTourId: 'add_money.steps', placement: 'bottom' },
    { id: 'question', targetTourId: 'add_money.question', placement: 'top' },
    { id: 'back', targetTourId: 'add_money.back', placement: 'bottom' },
  ]);

/** Guided Cash out (/money/cash-out): the targets are on every step. */
const cashOutTour = () =>
  translatedTour('cash_out', v1, 'cashOut', [
    { id: 'steps', targetTourId: 'cash_out.steps', placement: 'bottom' },
    { id: 'question', targetTourId: 'cash_out.question', placement: 'top' },
    { id: 'back', targetTourId: 'cash_out.back', placement: 'bottom' },
  ]);

/**
 * Help tab (/help, signed in), with the old FAQ tour's steps (search, topic, answers). "More ways to
 * get help" is left out: it is hidden on shared kiosk machines.
 */
const helpTour = () =>
  translatedTour('help', v1, 'help', [
    { id: 'search', targetTourId: 'help.search', placement: 'bottom' },
    { id: 'ask', targetTourId: 'help.ask', placement: 'bottom' },
    { id: 'questions', targetTourId: 'help.questions', placement: 'top' },
    { id: 'faq', targetTourId: 'help.faq', placement: 'top' },
  ]);

/** Help → Ask us (/help?tab=new): the ticket form (the old Support tour; v2: plain words, en + es). */
const askUsTour = () =>
  translatedTour('support', v2, 'askUs', [
    { id: 'form', targetTourId: 'tour.support.form', placement: 'bottom' },
    { id: 'submit', targetTourId: 'tour.support.submit', placement: 'top' },
  ]);

/** Me tab (/me). */
const meTour = () =>
  translatedTour('me', v1, 'me', [
    { id: 'profile', targetTourId: 'me.profile', placement: 'bottom' },
    { id: 'language', targetTourId: 'me.language', placement: 'top' },
    { id: 'logout', targetTourId: 'me.logout', placement: 'top' },
  ]);

/** My games (/money/games). v4: the card buttons are Add money / Take money out. */
const gamesTour = () =>
  translatedTour('games', 'v4', 'games', [
    { id: 'filters', targetTourId: 'tour.games.filters', placement: 'bottom' },
    { id: 'grid', targetTourId: 'tour.games.grid', placement: 'top' },
    { id: 'addMoney', targetTourId: 'tour.games.add_funds', placement: 'top' },
    { id: 'takeOut', targetTourId: 'tour.games.withdraw', placement: 'top' },
  ]);

const homeTour: TourDefinition = {
  id: 'home',
  version: v1,
  welcomeTitle: 'Home lobby',
  welcomeDescription: 'This is where you browse games and manage funds for the ones you already use.',
  steps: [
    {
      id: 'continue',
      targetTourId: 'tour.home.continue',
      title: 'Your games',
      description: 'Games you already linked show up here for a quick return.',
      placement: 'bottom',
    },
    {
      id: 'filters',
      targetTourId: 'tour.home.filters',
      title: 'Filters',
      description: 'Narrow the lobby by category or tag.',
      placement: 'bottom',
    },
    {
      id: 'search',
      targetTourId: 'tour.home.search',
      title: 'Search',
      description: 'Find a game platform by name.',
      placement: 'bottom',
    },
    {
      id: 'grid',
      targetTourId: 'tour.home.grid',
      title: 'Game lobby',
      description: 'Tap a tile to open details, deposit, or play.',
      placement: 'top',
    },
  ],
};

/** Deposit to games wizard — covers all 3 steps (cart → payment → checkout). */
const depositTour: TourDefinition = {
  id: 'finance_deposit',
  version: 'v3',
  welcomeTitle: 'Add funds to games',
  welcomeDescription: 'A walkthrough of every step: cart, payment method, and checkout.',
  steps: [
    {
      id: 'stepper',
      targetTourId: 'tour.deposit.stepper',
      title: 'Steps',
      description: 'Follow Add funds → Payment method → Checkout.',
      placement: 'bottom',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'cart',
      targetTourId: 'tour.deposit.cart',
      title: 'Your cart',
      description: 'Games you will fund appear here. Tap a row to set its amount.',
      placement: 'right',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'addGame',
      targetTourId: 'tour.deposit.add_game',
      title: 'Add a game',
      description: 'Open the picker to add more games to this order.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'amount',
      targetTourId: 'tour.deposit.amount_panel',
      title: 'Amount',
      description: 'Enter or pick a quick amount for the selected game.',
      placement: 'left',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'cta',
      targetTourId: 'tour.deposit.cta',
      title: 'Continue',
      description: 'When amounts look good, continue to check your wallet balance.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 1 },
    },
    {
      id: 'funding',
      targetTourId: 'tour.deposit.funding',
      title: 'How to fund',
      description: 'Your wallet balance must cover the full order before checkout.',
      placement: 'bottom',
      activate: { kind: 'deposit_wizard_step', step: 2 },
    },
    {
      id: 'review',
      targetTourId: 'tour.deposit.review_cta',
      title: 'Review checkout',
      description: 'Continue to the final review before confirming.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 2 },
    },
    {
      id: 'checkoutGames',
      targetTourId: 'tour.deposit.checkout_games',
      title: 'Games to fund',
      description: 'Double-check which games and amounts will receive funds.',
      placement: 'right',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
    {
      id: 'checkoutPayment',
      targetTourId: 'tour.deposit.checkout_payment',
      title: 'Payment summary',
      description: 'Confirm the wallet debit and total before submitting.',
      placement: 'left',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
    {
      id: 'confirm',
      targetTourId: 'tour.deposit.confirm_cta',
      title: 'Confirm',
      description: 'Submit to transfer funds from your wallet to the selected games.',
      placement: 'top',
      activate: { kind: 'deposit_wizard_step', step: 3 },
    },
  ],
};

const walletTour: TourDefinition = {
  id: 'finance_wallet',
  version: v2,
  welcomeTitle: 'Wallet Guide',
  welcomeDescription: 'Manage wallet balance, game funds, and payouts from one place.',
  steps: [
    {
      id: 'balanceCard',
      targetTourId: 'tour.wallet.balance_card',
      title: 'Your portfolio',
      description: 'See wallet balance, reserved funds, and totals in games.',
      placement: 'bottom',
    },
    {
      id: 'deposit',
      targetTourId: 'tour.wallet.deposit_section',
      title: 'Add funds',
      description: 'Top up your wallet or send funds straight into games.',
      placement: 'top',
    },
    {
      id: 'payout',
      targetTourId: 'tour.wallet.payout_card',
      title: 'Payout',
      description: 'Request a payout from your spendable wallet balance.',
      placement: 'top',
    },
    {
      id: 'inGames',
      targetTourId: 'tour.wallet.in_games',
      title: 'In your games',
      description: 'Linked games and balances — withdraw back to wallet here.',
      placement: 'top',
    },
    {
      id: 'activity',
      targetTourId: 'tour.wallet.recent_activity',
      title: 'Recent activity',
      description: 'Latest wallet movements; open Activity for the full history.',
      placement: 'top',
    },
  ],
};

const walletTopUpTour: TourDefinition = {
  id: 'wallet_topup',
  version: v1,
  welcomeTitle: 'Wallet deposit',
  welcomeDescription: 'Add funds to your wallet in three steps: amount, method, checkout.',
  steps: [
    {
      id: 'amount',
      targetTourId: 'tour.wallet_deposit.amount',
      title: 'Amount',
      description: 'Type an amount or tap a suggested chip.',
      placement: 'bottom',
    },
    {
      id: 'methods',
      targetTourId: 'tour.wallet_deposit.methods',
      title: 'Payment method',
      description: 'Choose how you want to pay (crypto and other available methods).',
      placement: 'top',
    },
    {
      id: 'checkout',
      targetTourId: 'tour.wallet_deposit.checkout',
      title: 'Checkout',
      description: 'Review the total and pay to credit your wallet.',
      placement: 'top',
    },
  ],
};

const walletPayoutTour: TourDefinition = {
  id: 'wallet_payout',
  version: v1,
  welcomeTitle: 'Wallet payout',
  welcomeDescription: 'Cash out from your wallet: amount, method, then confirm.',
  steps: [
    {
      id: 'amount',
      targetTourId: 'tour.wallet_payout.amount',
      title: 'Amount',
      description: 'Enter how much to withdraw, or use Max / quick amounts.',
      placement: 'bottom',
    },
    {
      id: 'methods',
      targetTourId: 'tour.wallet_payout.methods',
      title: 'Payout method',
      description: 'Pick where the funds should go.',
      placement: 'top',
    },
    {
      id: 'checkout',
      targetTourId: 'tour.wallet_payout.checkout',
      title: 'Confirm',
      description: 'Review the request and submit your payout.',
      placement: 'top',
    },
  ],
};

const transactionsTour: TourDefinition = {
  id: 'transactions',
  version: v2,
  welcomeTitle: 'Activity Guide',
  welcomeDescription: 'Filter and review your deposit and withdrawal history.',
  steps: [
    {
      id: 'filters',
      targetTourId: 'tour.transactions.filters',
      title: 'Filters',
      description: 'Filter by funds in/out, pending, and payment status.',
      placement: 'bottom',
    },
    {
      id: 'list',
      targetTourId: 'tour.transactions.list',
      title: 'Transaction list',
      description: 'Tap a row to see details (or open a payment link if available).',
      placement: 'top',
    },
    {
      id: 'pagination',
      targetTourId: 'tour.transactions.pagination',
      title: 'Pagination',
      description: 'Move through pages when you have a long history.',
      placement: 'top',
    },
  ],
};

const settingsTour: TourDefinition = {
  id: 'settings',
  version: v1,
  welcomeTitle: 'Settings Guide',
  welcomeDescription: 'Manage your credentials and personal information.',
  steps: [
    {
      id: 'credentials',
      targetTourId: 'tour.settings.credentials',
      title: 'Credentials',
      description: 'View your username and request new credentials if needed.',
      placement: 'bottom',
    },
    {
      id: 'contact',
      targetTourId: 'tour.settings.contact',
      title: 'Email & phone',
      description: 'Link your email/phone to secure your account.',
      placement: 'top',
    },
    {
      id: 'profile',
      targetTourId: 'tour.settings.profile',
      title: 'Profile',
      description: 'Edit your basic info and address details here.',
      placement: 'top',
    },
  ],
};

const accountSecurityTour: TourDefinition = {
  id: 'account_security',
  version: v1,
  welcomeTitle: 'Account security',
  welcomeDescription: 'Reissue login credentials and manage two-factor authentication.',
  steps: [
    {
      id: 'reissue',
      targetTourId: 'tour.security.reissue',
      title: 'New credentials',
      description: 'Request a new username and temporary password by SMS/email (limited).',
      placement: 'bottom',
    },
    {
      id: 'twoFactor',
      targetTourId: 'tour.security.two_factor',
      title: 'Two-factor auth',
      description: 'Enable or disable 2FA to protect sign-in.',
      placement: 'top',
    },
  ],
};

const animationsTour: TourDefinition = {
  id: 'animations',
  version: v1,
  welcomeTitle: 'Animation settings',
  welcomeDescription: 'Control motion across the app and per page.',
  steps: [
    {
      id: 'global',
      targetTourId: 'tour.animations.global',
      title: 'All pages',
      description: 'Master switch — turns motion off everywhere.',
      placement: 'bottom',
    },
    {
      id: 'perPage',
      targetTourId: 'tour.animations.per_page',
      title: 'Individual pages',
      description: 'Toggle animations for specific screens when the master switch is on.',
      placement: 'top',
    },
  ],
};
